import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { InfoCircleFill } from 'react-bootstrap-icons';
import { GroupStandingsTieBreakerEnum } from '../../../../../types/enums';
import {
  getSelectableGroupStandingsTieBreakers,
  GROUP_STANDINGS_TIE_BREAKER_DESCRIPTION_KEYS,
  GROUP_STANDINGS_TIE_BREAKER_LABEL_KEYS,
  isCriterionAfterCoinToss,
  isTeamOnlyGroupStandingsTieBreaker,
} from '../../../../../utils/groupStandingsTieBreakers';
import './GroupStandingsTieBreakerEditor.scss';

type GroupStandingsTieBreakerEditorProps = {
  value: GroupStandingsTieBreakerEnum[];
  onChange: (nextValue: GroupStandingsTieBreakerEnum[]) => void;
  disabled?: boolean;
  /**
   * Class editor: true = teams, false = singles/doubles (team rows N/A-greyed).
   * Parent editors leave undefined so team rows stay live.
   */
  isTeamsClass?: boolean;
};

export const GroupStandingsTieBreakerEditor = ({
  value,
  onChange,
  disabled = false,
  isTeamsClass,
}: GroupStandingsTieBreakerEditorProps) => {
  const { t } = useTranslation();
  const [dragging, setDragging] =
    useState<GroupStandingsTieBreakerEnum | null>(null);
  const [isAutoReordering, setIsAutoReordering] = useState(false);
  const previousOrderRef = useRef<string>('');
  const animateTimeoutRef = useRef<number | null>(null);

  const orderedItems = useMemo(() => value || [], [value]);
  const currentOrderKey = useMemo(() => orderedItems.join('|'), [orderedItems]);
  const [pendingAdd, setPendingAdd] = useState<GroupStandingsTieBreakerEnum | ''>('');

  const selectableTieBreakers = useMemo(
    () => getSelectableGroupStandingsTieBreakers(),
    [],
  );

  const availableToAdd = useMemo(
    () => selectableTieBreakers.filter((item) => !orderedItems.includes(item)),
    [orderedItems, selectableTieBreakers],
  );

  useEffect(() => {
    if (!previousOrderRef.current) {
      previousOrderRef.current = currentOrderKey;
      return;
    }

    if (previousOrderRef.current !== currentOrderKey) {
      setIsAutoReordering(true);
      if (animateTimeoutRef.current) {
        window.clearTimeout(animateTimeoutRef.current);
      }
      animateTimeoutRef.current = window.setTimeout(() => {
        setIsAutoReordering(false);
      }, 360);
    }

    previousOrderRef.current = currentOrderKey;
  }, [currentOrderKey]);

  useEffect(() => {
    return () => {
      if (animateTimeoutRef.current) {
        window.clearTimeout(animateTimeoutRef.current);
      }
    };
  }, []);

  const moveItem = (from: number, to: number) => {
    if (disabled || from === to || from < 0 || to < 0) return;
    const next = [...orderedItems];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  const removeItem = (item: GroupStandingsTieBreakerEnum) => {
    if (disabled) return;
    onChange(orderedItems.filter((entry) => entry !== item));
  };

  const addItem = () => {
    if (disabled || !pendingAdd) return;
    onChange([...orderedItems, pendingAdd]);
    setPendingAdd('');
  };

  return (
    <div
      className="group-standings-tie-breaker-editor"
      data-testid="group-standings-tie-breaker-editor"
      data-disabled={disabled}
      data-auto-reordering={isAutoReordering}
    >
      {orderedItems.map((item, index) => {
        const labelKey = GROUP_STANDINGS_TIE_BREAKER_LABEL_KEYS[item];
        const descriptionKey = GROUP_STANDINGS_TIE_BREAKER_DESCRIPTION_KEYS[item];
        const isTeamOnly = isTeamOnlyGroupStandingsTieBreaker(item);
        const isAfterCoinToss = isCriterionAfterCoinToss(orderedItems, index);
        const isNotApplicableOnClass =
          isTeamsClass === false && isTeamOnly;
        const isInactive = isAfterCoinToss || isNotApplicableOnClass;

        return (
          <div
            key={item}
            className="group-standings-tie-breaker-editor__row"
            data-disabled={disabled}
            data-dragging={dragging === item}
            data-inactive={isInactive}
            data-team-only={isTeamOnly}
            draggable={!disabled}
            onDragStart={() => setDragging(item)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => {
              if (!dragging || dragging === item) return;
              const from = orderedItems.indexOf(dragging);
              const to = orderedItems.indexOf(item);
              moveItem(from, to);
              setDragging(null);
            }}
            onDragEnd={() => setDragging(null)}
          >
            <div className="group-standings-tie-breaker-editor__label">
              <span className="group-standings-tie-breaker-editor__drag-handle">::</span>
              <span className="group-standings-tie-breaker-editor__index">
                {index + 1}.
              </span>
              <span>{t(labelKey)}</span>
              {isTeamOnly ? (
                <span className="group-standings-tie-breaker-editor__badge">
                  {t('groupStandingsTieBreaker-teamEventBadge')}
                </span>
              ) : null}
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>{t(descriptionKey)}</Tooltip>}
              >
                <InfoCircleFill
                  size={14}
                  className="group-standings-tie-breaker-editor__info-icon"
                />
              </OverlayTrigger>
              {isAfterCoinToss ? (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      {t('groupStandingsTieBreaker-afterCoinTossHint')}
                    </Tooltip>
                  }
                >
                  <InfoCircleFill
                    size={14}
                    className="group-standings-tie-breaker-editor__info-icon group-standings-tie-breaker-editor__info-icon--inactive"
                    data-testid="after-coin-toss-info"
                  />
                </OverlayTrigger>
              ) : null}
              {isNotApplicableOnClass ? (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      {t('groupStandingsTieBreaker-teamCriterionNotApplicable')}
                    </Tooltip>
                  }
                >
                  <InfoCircleFill
                    size={14}
                    className="group-standings-tie-breaker-editor__info-icon group-standings-tie-breaker-editor__info-icon--inactive"
                    data-testid="team-na-info"
                  />
                </OverlayTrigger>
              ) : null}
            </div>
            <div className="group-standings-tie-breaker-editor__actions">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={disabled || index === 0}
                aria-label={t('groupStandingsTieBreaker-moveUp', { label: t(labelKey) })}
                onClick={() => moveItem(index, index - 1)}
              >
                ↑
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={disabled || index === orderedItems.length - 1}
                aria-label={t('groupStandingsTieBreaker-moveDown', { label: t(labelKey) })}
                onClick={() => moveItem(index, index + 1)}
              >
                ↓
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                disabled={disabled}
                aria-label={t('groupStandingsTieBreaker-removeCriterion', { label: t(labelKey) })}
                onClick={() => removeItem(item)}
              >
                ×
              </button>
            </div>
          </div>
        );
      })}
      {availableToAdd.length ? (
        <div className="group-standings-tie-breaker-editor__add">
          <select
            className="form-select form-select-sm group-standings-tie-breaker-editor__add-select"
            value={pendingAdd}
            disabled={disabled}
            onChange={(event) =>
              setPendingAdd(event.target.value as GroupStandingsTieBreakerEnum | '')
            }
          >
            <option value="">{t('groupStandingsTieBreaker-selectCriterionPlaceholder')}</option>
            {availableToAdd.map((item) => (
              <option key={item} value={item}>
                {t(GROUP_STANDINGS_TIE_BREAKER_LABEL_KEYS[item])}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            disabled={disabled || !pendingAdd}
            onClick={addItem}
          >
            {t('groupStandingsTieBreaker-addCriterion')}
          </button>
        </div>
      ) : null}
    </div>
  );
};
